import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import tw from 'twin.macro';
import styled from '@emotion/styled';
import { Checkbox, Switch, Table } from 'antd';
import Legend from 'components/molecules/Legend';
import { colorConstants } from 'config/colorConstants';
import { getBestWeeksColumns, getBestWeeksDrillDownColumns } from 'utils/getRecommendationTableColumns';
import { useFetchBestWeeksData } from 'hooks/promoRecommendation/useFetchBestWeeksData';
import { FilterContextData } from 'contexts/FilterContext';
import {
    calculateContainerHeight,
    formatNumbers,
    generateDefaultApplyFilter,
    getConfig,
    getIntersectionOnApplyFilter,
    parseAttributeType,
} from 'utils/helperMethods';
import { UserContext } from 'contexts/ConfigContext';
import InfiniteScroller from 'components/molecules/InfiniteScroller';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { getBestWeeksDrillDownExportData, getBestweeksExcelData } from 'parser/graphToExcelDownloadParser';
import { ConfigKeys } from 'utils/Enums';
import SelectWithInfiniteScroller from 'components/atoms/SelectWithInfiniteScroller';
import { CustomSelect } from 'components/pages/GrowthAnalysis';
import _ from 'lodash';
import { useApplyFilterMutation } from 'hooks/general/useApplyFilterMutation';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { bestWeeksUrls } from 'config/urls';
import { subHeaderClass } from '../GrowthAnalysis/ExpandedView';
import { useDebounceLoading } from 'hooks/general/useDebounceLoading';
import { useFillHeightToFitScreen } from 'hooks/general/useFillHeightToFitScreen';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';

const drillDownTableId = 'drilldown-table-id';

const switchTitleArray = [
    { label: 'Metric Value', key: 'metric_value' },
    { label: 'Final Score', key: 'final_score' },
];

export const height = calculateContainerHeight(2); //adding a buffer of 200

const BestWeeksSection = () => {
    const {
        allDimAndAttributeTypeList,
        allDimAndAttributeType,
        idForRestOfComponent,
        setHideGlobalTimeFilterComponent,
        screenFilterConfig,
    } = useContext(FilterContextData);
    const [bestWeeksFilterItems, setBestWeeksFilterItems] = useState([]);
    const [selectedFilterItem, setSelectedFilterItem] = useState<any>(null);
    const [openFilterDropdown, setOpenFilterDropdown] = useState(false);

    const { appConfig } = useContext(UserContext);
    const bestWeeksConfig = getConfig(ConfigKeys.BEST_WEEKS, appConfig);
    const commonConfig = getConfig('commonConfig', appConfig);
    const decimalForNonPercentage = commonConfig?.decimalForNonPercentage;
    const decimalForPercentage = commonConfig?.decimalForPercentage;
    const dimensionConfig = bestWeeksConfig?.dimensionConfig;
    const drillDownTableConfig = bestWeeksConfig?.drilldownTableConfig;
    const rankingCriteriaOptions = bestWeeksConfig.rankingCriteriaOptions;
    const currentScreenFeatureName = screenFilterConfig?.dimensionListKey || 'default';

    // Show products w 52W sales
    const [hideGapInSales, setHideGapInSales] = useState(false);
    const [rankingCriteria, setRankingCriteria] = useState<any>(rankingCriteriaOptions[0]);
    const [drillDownVariable, setDrillDownVariable] = useState<any>(null);
    const [showFinalScore, setShowFinalScore] = useState(false);
    const {
        error: bestWeeksError,
        fetchBestWeeksData,
        data: bestWeeksData,
        count,
        loading: bestWeeksDataLoading,
    } = useFetchBestWeeksData();

    const {
        data: applyFilterData,
        loading: applyFilterLoading,
        error: applyFilterError,
        applyFilterMutate,
    } = useApplyFilterMutation({ onComplete: onApplyFilterComplete, onError: onApplyFilterError });

    const offsetRef = useRef(0);
    const bestWeeksTabelRef = useRef<any>();
    const applyFilterObj = useRef<any>(null);

    const mainSectionHeight = useRef<number | null>(0);
    const refTable = useRef(null);

    //this hook with give height to fill the screen height
    mainSectionHeight.current = useFillHeightToFitScreen({
        id: drillDownTableId,
        height: mainSectionHeight.current,
        buffer: 80,
        // triggerDummyRender: true,
    });

    const { loading: bestWeeksDrillDownLoading, data: bestWeeksDrillDownData } = useCommonApiHandler({
        queryKey: ['best-weeks-drillDown', { ...drillDownVariable }],
        method: 'post',
        variables: { ...drillDownVariable },
        queryConfig: {
            enabled: Boolean(drillDownVariable?.ranking_criteria),
        },
        url: bestWeeksUrls.getBestWeeksDrillDownDataUrl,
        parserFunction: (res) => {
            const metricValue = res?.[switchTitleArray[0].key]?.map((item: any, index: number) => {
                return {
                    period_no: item?.period_no,
                    key: switchTitleArray[0].key + index + item?.period_no,
                    [rankingCriteria?.value]: formatNumbers(
                        item?.value,
                        '',
                        '',
                        decimalForNonPercentage,
                        decimalForPercentage,
                        false,
                    ),
                    [rankingCriteria?.value + '_original_value']: item?.value,
                    year: item?.year,
                };
            });
            const finalScore = res?.[switchTitleArray[1].key]?.map((item: any, index: number) => {
                return {
                    period_no: item?.period_no,
                    key: switchTitleArray[1].key + index + item?.period_no,
                    score: formatNumbers(item?.score, '', '', decimalForNonPercentage, decimalForPercentage, false),
                    rank: formatNumbers(item?.rank, '', '', 0, 0, false),
                    score_original_value: item?.score,
                    rank_original_value: item?.rank,
                };
            });

            const response = {
                [switchTitleArray[0].key]: metricValue,
                [switchTitleArray[1].key]: finalScore,
            };

            return response;
        },
    });

    const dynamicTableHeaders = useMemo(() => {
        const dynamicHeaders = showFinalScore
            ? [
                  { label: 'Score', key: 'score' },
                  { label: 'Rank', key: 'rank' },
              ]
            : [
                  { label: 'Year', key: 'year', sorting: false },
                  { label: rankingCriteria?.label, key: rankingCriteria?.value },
              ];
        const dynamicExportHeaders = showFinalScore
            ? [
                  { label: 'Score', key: 'score_original_value' },
                  { label: 'Rank', key: 'rank_original_value' },
              ]
            : [
                  { label: 'Year', key: 'year', sorting: false },
                  { label: rankingCriteria?.label, key: rankingCriteria?.value + '_original_value' },
              ];
        return {
            headers: [...(drillDownTableConfig ?? []), ...dynamicHeaders],
            exportHeaders: [...(drillDownTableConfig ?? []), ...dynamicExportHeaders],
        };
    }, [showFinalScore, bestWeeksDrillDownData]);

    const memoisedDrillDownColumns = useMemo(() => {
        return getBestWeeksDrillDownColumns(dynamicTableHeaders?.headers);
    }, [bestWeeksDrillDownData, dynamicTableHeaders]);

    const drillDownExportData = useMemo(() => {
        return getBestWeeksDrillDownExportData(
            bestWeeksDrillDownData?.[showFinalScore ? switchTitleArray[1].key : switchTitleArray[0].key],
            dynamicTableHeaders?.exportHeaders,
        );
    }, [bestWeeksDrillDownData, dynamicTableHeaders]);

    // for price tracking we want to hide time period filter selection in the global filter
    useEffect(() => {
        setHideGlobalTimeFilterComponent(true);
        // resetting the state
        return () => {
            setHideGlobalTimeFilterComponent(false);
        };
    }, []);

    useEffect(() => {
        if (
            allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !allDimAndAttributeTypeList['loading'] &&
            allDimAndAttributeTypeList['dimentions'] &&
            !selectedFilterItem
        ) {
            const data = parseAttributeType(allDimAndAttributeTypeList['dimentions'])?.filter(
                (item: any) => !dimensionConfig?.excludedDimensions.includes(item.dimension),
            );
            const dim1Attributes = allDimAndAttributeTypeList['dimentions'][0];
            if (data.length) {
                setBestWeeksFilterItems(data);
                setSelectedFilterItem(data[dim1Attributes.attribute.length - 1]); // select last level from dim1 attributes as default
                setSelectedFilterItem(
                    data.find((item: any) => item.label === dimensionConfig.defaultDimension) || data[0],
                );
            }
        }
    }, [allDimAndAttributeTypeList?.loading, allDimAndAttributeType]);

    useEffect(() => {
        if (
            allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !allDimAndAttributeTypeList['loading'] &&
            allDimAndAttributeTypeList['dimentions']
        ) {
            offsetRef.current = 0;
            fetchBestWeeksData({
                variables: {
                    dimension: selectedFilterItem?.dimension,
                    limit: bestWeeksConfig?.limit,
                    offset: offsetRef.current,
                    attributeName: selectedFilterItem?.label?.replace(' ', '_'),
                    filters: JSON.stringify(idForRestOfComponent),
                    rankingCriteria: rankingCriteria?.value,
                    hideGapInSales,
                },
            });
        }
    }, [
        selectedFilterItem,
        allDimAndAttributeTypeList?.loading,
        allDimAndAttributeType,
        idForRestOfComponent,
        hideGapInSales,
        rankingCriteria,
    ]);

    const memoisedBestWeeksColumns = useMemo(() => {
        return getBestWeeksColumns(
            bestWeeksData?.bestWeeksMapping,
            selectedFilterItem,
            setSelectedFilterItem,
            bestWeeksFilterItems,
            openFilterDropdown,
            setOpenFilterDropdown,
        );
    }, [bestWeeksData, selectedFilterItem, openFilterDropdown]);

    const infiniteScrollOnNext = (loading: boolean) => {
        if (!loading) {
            const newOffset = offsetRef.current + bestWeeksConfig.limit;
            {
                fetchBestWeeksData({
                    variables: {
                        dimension: selectedFilterItem?.dimension,
                        limit: bestWeeksConfig?.limit,
                        offset: newOffset,
                        attributeName: selectedFilterItem?.label?.replace(' ', '_'),
                        filters: JSON.stringify(idForRestOfComponent),
                        rankingCriteria: rankingCriteria?.value,
                        hideGapInSales,
                    },
                });
            }
            offsetRef.current = newOffset;
        }
    };

    const commonLoading = bestWeeksDataLoading || allDimAndAttributeTypeList?.loading;
    const { loading: commonDrillDownLoading } = useDebounceLoading({
        loadingDependency: bestWeeksDrillDownLoading || applyFilterLoading,
    });

    const resetDrillDownVariable = () => {
        setDrillDownVariable(null);
        applyFilterObj.current = null;
    };

    useEffect(() => {
        resetDrillDownVariable();
    }, [selectedFilterItem, idForRestOfComponent]);

    const completeExcelData = useMemo(() => {
        return getBestweeksExcelData(selectedFilterItem?.label, bestWeeksData);
    }, [selectedFilterItem, bestWeeksData]);

    //called when clicked on row
    function onRowClickHandler(item: any) {
        const applyFilterItem = {
            attributeExId: item?.attributeName,
            attributeDesc: item?.attributeName,
            dimension: selectedFilterItem.dimension,
            attributeType: selectedFilterItem?.label?.replace(' ', '_'),
        };
        applyFilterObj.current = applyFilterItem;
        prepareApplyFilterVariableAndExecute();
    }

    //called to prepare apply filter variable and execute
    function prepareApplyFilterVariableAndExecute() {
        const defaultApplyFilterGenerated = generateDefaultApplyFilter(idForRestOfComponent);
        const tempApplyFilterVairable: any = _.cloneDeep(defaultApplyFilterGenerated);
        if (applyFilterObj.current?.dimension === 'dim1') {
            tempApplyFilterVairable.jsonstring_dim1.inclusion = [
                {
                    ...applyFilterObj.current,
                },
            ];
        } else if (applyFilterObj.current?.dimension === 'dim2') {
            tempApplyFilterVairable.jsonstring_dim2.inclusion = [
                {
                    ...applyFilterObj.current,
                },
            ];
        } else {
            tempApplyFilterVairable.jsonstring_dim3.inclusion = [
                {
                    ...applyFilterObj.current,
                },
            ];
        }
        tempApplyFilterVairable.jsonstring_dim1 = JSON.stringify(tempApplyFilterVairable.jsonstring_dim1);
        tempApplyFilterVairable.jsonstring_dim2 = JSON.stringify(tempApplyFilterVairable.jsonstring_dim2);
        tempApplyFilterVairable.jsonstring_dim3 = JSON.stringify(tempApplyFilterVairable?.jsonstring_dim3 || '{}');
        tempApplyFilterVairable.jsonstring_dim4 = JSON.stringify(tempApplyFilterVairable?.jsonstring_dim4 || '{}');
        applyFilterMutate({ variables: tempApplyFilterVairable });
    }

    //called when apply filter is complete
    function onApplyFilterComplete(payload: any) {
        const tempApplyFilterResponse = getIntersectionOnApplyFilter(idForRestOfComponent, payload);
        setDrillDownVariable({
            filters: tempApplyFilterResponse,
            ranking_criteria: rankingCriteria?.value,
            feature_name: currentScreenFeatureName,
        });
    }

    //called when apply filter is error
    function onApplyFilterError() {
        applyFilterObj.current = null;
        console.log('logging error');
    }

    return (
        <div className="bg-white rounded-lg p-4">
            <div className="mb-4">
                <h2 className="text-xl font-semibold text-gray-800">Best Weeks</h2>
            </div>

            <div className="mt-4">
                <div className="mb-2 flex justify-between items-center">
                    {/* TODO  */}
                    {/* <p className="text-promoColour font-xs">Highlighted cells are promoted weeks</p> */}
                    <div className={`flex items-center ${bestWeeksDataLoading ? 'pointer-events-none' : ''}`}>
                        <Checkbox
                            checked={hideGapInSales}
                            onChange={() => {
                                setHideGapInSales((prev) => !prev);
                                resetDrillDownVariable();
                            }}
                        />
                        <span style={{ marginLeft: 8 }}>Show products w 52W sales</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <div className="mr-2">
                            <Legend
                                data={[
                                    {
                                        label: 'Highest Ranking',
                                        color: colorConstants.limeGreen,
                                        style: { width: 8, height: 8, borderRadius: 2 },
                                    },
                                    {
                                        label: 'Lowest Ranking',
                                        color: colorConstants.red,
                                        style: { width: 8, height: 8, borderRadius: 2 },
                                    },
                                ]}
                            />
                        </div>
                    </div>
                    <div className="flex justify-center items-center gap-2">
                        <div className="flex gap-2 justify-center items-center ml-2">
                            <span className="text-xs text-promoColour">Ranking Criteria</span>
                            <CustomSelect
                                showSearch={false}
                                filterOption={false}
                                placeholder={`Select Multiple...`}
                                maxTagCount="responsive"
                                className="dropdown-height !h-8 !w-48"
                                showArrow
                                onChange={(val: any, item: any) => {
                                    setDrillDownVariable(
                                        drillDownVariable
                                            ? { ...drillDownVariable, ranking_criteria: item?.value }
                                            : null,
                                    );
                                    setRankingCriteria(item);
                                }}
                                value={rankingCriteria?.value}
                                options={rankingCriteriaOptions}
                            />
                        </div>
                        <div className={`${commonLoading ? 'pointer-events-none' : ''}`}>
                            <DownloadExcel
                                title={'Best Weeks Data'}
                                excelData={[]}
                                headers={[]}
                                completeDataWithHeaders={completeExcelData}
                            />
                        </div>
                    </div>
                </div>

                <div className="flex flex-col">
                    <div id="best-weeks-table" ref={bestWeeksTabelRef}>
                        <InfiniteScroller
                            data={bestWeeksData?.weeksData}
                            loading={commonLoading}
                            hasMore={bestWeeksData?.weeksData?.length < count || commonLoading}
                            height={
                                bestWeeksData?.weeksData?.length < count || offsetRef.current
                                    ? bestWeeksData?.weeksData && !bestWeeksError
                                        ? mainSectionHeight.current ?? undefined
                                        : isNaN(Number(mainSectionHeight.current))
                                        ? undefined
                                        : Number(mainSectionHeight.current) - 1
                                    : 'auto' //there should be a change in height when its loading and not loading, thats the reason for subtracting 1
                            }
                            onNext={() => infiniteScrollOnNext(commonLoading)}
                        >
                            <CustomTable
                                id={drillDownTableId}
                                onRow={(record, index) => ({
                                    onClick: (event) => {
                                        onRowClickHandler(record);
                                    },
                                    style: {
                                        cursor: 'pointer',
                                    },
                                })}
                                style={{
                                    opacity: commonLoading ? '0.3' : 1,
                                    pointerEvents: commonLoading ? 'none' : 'auto',
                                }}
                                pagination={false}
                                scroll={{ x: '100%' }}
                                sticky
                                columns={memoisedBestWeeksColumns}
                                dataSource={bestWeeksData?.weeksData || null}
                                // locale={{
                                //     emptyText: expandedTableDataError ? (
                                //         <ErrorBox msg={String(expandedTableDataError)} />
                                //     ) : (
                                //         ''
                                //     ),
                                // }}
                                bordered
                            />
                        </InfiniteScroller>
                    </div>
                    {applyFilterObj.current && (
                        <div ref={refTable} className="my-2">
                            <div className="flex mb-2 items-center justify-between">
                                <div className={subHeaderClass}>
                                    {rankingCriteria?.label} - {applyFilterObj.current?.attributeExId}
                                </div>
                                <div className=" flex gap-2 items-center">
                                    <p className="font-xs text-blackAlt mr-1">{switchTitleArray[0].label}</p>
                                    <Switch
                                        checked={showFinalScore}
                                        onChange={() => setShowFinalScore((prev) => !prev)}
                                    />
                                    <p className="font-xs text-blackAlt mr-1">{switchTitleArray[1].label}</p>

                                    <CopyToClipBoard
                                        removeScrollbar
                                        elementClassToRemoveScrollbar="ant-table-body"
                                        refOfComponent={refTable}
                                    />
                                    <DownloadExcel
                                        titleArray={[[switchTitleArray[showFinalScore ? 1 : 0].label]]}
                                        title={'Best Weeks drillDown Data'}
                                        excelData={drillDownExportData?.result}
                                        headers={drillDownExportData?.headers}
                                    />
                                </div>
                            </div>
                            <DrillDownCustomTable
                                style={{
                                    opacity: commonDrillDownLoading ? '0.3' : 1,
                                    pointerEvents: commonDrillDownLoading ? 'none' : 'auto',
                                }}
                                pagination={false}
                                scroll={{ x: '100%', y: height }}
                                sticky
                                loading={commonDrillDownLoading}
                                columns={memoisedDrillDownColumns}
                                dataSource={
                                    bestWeeksDrillDownData?.[
                                        showFinalScore ? switchTitleArray[1].key : switchTitleArray[0].key
                                    ]
                                }
                                // locale={{
                                //     emptyText: expandedTableDataError ? (
                                //         <ErrorBox msg={String(expandedTableDataError)} />
                                //     ) : (
                                //         ''
                                //     ),
                                // }}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BestWeeksSection;
const CustomTable = styled(Table)`
    .ant-table-thead > tr > th {
        background: ${colorConstants.grey};
        padding: 0px;
        height: 44px;
    }
    .ant-table-body {
        overflow-y: auto !important;
    }
    .ant-table-sticky-scroll {
        position: static;
    }
    .bold {
        font-weight: bold;
    }

    .ant-table-thead
        > tr
        > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not(
            [colspan]
        ):before {
        background: none;
    }
    .ant-table-column-sorter {
        position: relative;
        left: -10px;
    }
    .ant-table-column-sorter-down.active,
    .ant-table-column-sorter-up.active {
        ${tw`text-purpleFont`};
    }
    ${tw`border-t`}
    border-bottom: unset;
    ${tw`border border-borderLinePurpuleAlt`}
`;

const DrillDownCustomTable = styled(Table)`
    .ant-table-sticky-scroll {
        position: static;
    }
    .ant-table-thead > tr > th {
        background: ${colorConstants.grey};
        height: 68px;
    }
    .ant-table-header col:not(:last-child),
    .ant-table-thead
        > tr
        > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not(
            [colspan]
        ):before {
        background: none;
    }
    .ant-table-column-sorter {
        position: relative;
        left: -10px;
    }
    .ant-table-column-sorter-down.active,
    .ant-table-column-sorter-up.active {
        ${tw`text-purpleFont`};
    }
    border: ${'1px solid #DBDFE9'};
`;
